import React, { useState, useMemo } from 'react';
import { LottoDraw } from '../types';
import { Download, Table, AlertCircle, Filter, X } from 'lucide-react';
import { motion } from 'framer-motion';
import LottoBall from './LottoBall';

interface HistoryViewProps {
  history: LottoDraw[];
  loading: boolean;
  onRefresh: () => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ history, loading, onRefresh }) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const filteredHistory = useMemo(() => {
    return history.filter(draw => {
      // Normalize dates for comparison (assuming draw.date is parsable or formatted as YYYY-MM-DD)
      const drawDate = new Date(draw.date);
      if (isNaN(drawDate.getTime())) return true; // Keep if date is invalid to be safe
      
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && drawDate < start) return false;
      if (end && drawDate > end) return false;

      return true;
    });
  }, [history, startDate, endDate]);

  const handleDownload = () => {
    if (filteredHistory.length === 0) return;
    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(filteredHistory, null, 2)
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = "nlcb_lotto_history.json";
    link.click();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto p-8 text-center bg-slate-800/50 rounded-2xl border border-slate-700 animate-pulse">
        <Table className="w-12 h-12 mx-auto text-slate-600 mb-4" />
        <h2 className="text-xl text-slate-300 font-bold mb-2">Retrieving Historical Data...</h2>
        <p className="text-slate-500">Scanning NLCB archives for past results.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
        <div>
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-slate-100 flex items-center gap-2">
            <Table className="text-yellow-500" />
            Last {history.length} NLCB Results
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Historical archive of Lotto Plus draws.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 transition"
          >
            Refresh
          </button>
          <button
            onClick={handleDownload}
            disabled={filteredHistory.length === 0}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={18} />
            Export Data
          </button>
        </div>
      </div>

      {/* Date Filter Section */}
      <div className="bg-slate-800/40 border border-slate-700 p-4 rounded-xl flex flex-col sm:flex-row items-end sm:items-center gap-4">
        <div className="flex items-center gap-2 text-slate-400 text-sm font-bold uppercase tracking-wider">
           <Filter size={16} /> Filter by Date
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex flex-col gap-1">
             <label htmlFor="start-date" className="text-xs text-slate-500">Start Date</label>
             <input 
               type="date" 
               id="start-date"
               value={startDate}
               onChange={(e) => setStartDate(e.target.value)}
               className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-yellow-500/50"
             />
          </div>
          <div className="flex flex-col gap-1">
             <label htmlFor="end-date" className="text-xs text-slate-500">End Date</label>
             <input 
               type="date" 
               id="end-date"
               value={endDate}
               onChange={(e) => setEndDate(e.target.value)}
               className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-yellow-500/50"
             />
          </div>
          {(startDate || endDate) && (
            <button 
              onClick={clearFilters}
              className="mt-5 p-2 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 transition-colors"
              title="Clear Filters"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="ml-auto text-xs text-slate-500">
           Showing {filteredHistory.length} of {history.length} records
        </div>
      </div>

      {filteredHistory.length === 0 ? (
        <div className="p-8 text-center border border-slate-800 rounded-xl bg-slate-900/30">
          <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No results found for the selected criteria.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-800/40 backdrop-blur-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/80 text-slate-400 text-sm uppercase tracking-wider">
                <th className="p-4 border-b border-slate-700">Date</th>
                <th className="p-4 border-b border-slate-700">Draw #</th>
                <th className="p-4 border-b border-slate-700">Winning Numbers</th>
                <th className="p-4 border-b border-slate-700">PB</th>
                <th className="p-4 border-b border-slate-700">Multiplier</th>
                <th className="p-4 border-b border-slate-700 text-right">Jackpot</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {filteredHistory.map((draw, idx) => (
                <motion.tr 
                  key={idx} 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="hover:bg-slate-700/30 transition-colors text-slate-300"
                >
                  <td className="p-4 font-mono text-sm">{draw.date}</td>
                  <td className="p-4 font-mono text-slate-400">
                    {draw.drawNumber ? (
                      <span className="px-2 py-1 bg-slate-800 rounded text-xs border border-slate-700">#{draw.drawNumber}</span>
                    ) : (
                      <span className="opacity-50">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {draw.numbers.map((n, i) => (
                        <LottoBall key={i} number={n} size="sm" />
                      ))}
                    </div>
                  </td>
                  <td className="p-4">
                    <LottoBall number={draw.powerball} powerball size="sm" />
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 font-bold font-mono text-sm">
                      {draw.multiplier || '—'}
                    </span>
                  </td>
                  <td className="p-4 text-right font-mono text-green-400">
                    {draw.jackpot}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 text-center text-xs text-slate-500 bg-slate-900/50 border-t border-slate-800">
            Note: Displaying retrieved results. Complete history may require direct database access.
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryView;